import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ImprovementProposal } from '../api/types.js';
import { useAidevIssues, useDecideImprovement, useImprovements } from '../hooks/use-improvements.js';
import { Button } from './button.js';
import { SettingsSection } from './settings-section.js';

// « Proposals for aidev »: improvements the server's sessions want made to aidev (ADR-025). Nothing is filed until the
// operator opens one as an issue, after reading and editing it: the text was written by a session that may have read
// anything, and the issue is seen by everyone with access to the aidev repository. Plain text only.

const EDGE: Record<ImprovementProposal['kind'], string> = { security: 'var(--color-error)', bug: 'var(--color-warn)', enhancement: 'var(--color-brand)' };
const KIND: Record<ImprovementProposal['kind'], string> = { security: 'Security', bug: 'Bug', enhancement: 'Improvement' };

function ago(iso: string): string {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (m < 60) return `${Math.max(m, 1)} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}

function Body({ text }: { text: string }) {
  const [all, setAll] = useState(false);
  const long = text.length > 420 || text.split('\n').length > 6;
  return (
    <div className="mt-2">
      <p className={`max-w-[72ch] whitespace-pre-wrap break-words text-sm ${!all && long ? "line-clamp-5" : ""}`} style={{ color: 'var(--color-text-2)' }}>{text}</p>
      {long && (
        <button type="button" onClick={() => setAll(!all)} aria-expanded={all} className="mt-1 py-0.5 text-xs hover:underline focus-visible:outline focus-visible:outline-2" style={{ color: 'var(--color-brand-light)' }}>
          {all ? 'Show less' : 'Show all'}
        </button>
      )}
    </div>
  );
}

const field = 'block w-full rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus-visible:outline focus-visible:outline-2';
const fieldStyle = { backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border-2)', color: 'var(--color-text)' };

/**
 * New issue or duplicate of an open one: several servers often report the same thing. A duplicate is still filed (the
 * report and where it came from stay on record), then closed as a duplicate of the issue kept, which gets a note.
 */
function DuplicateChoice({ serverId, value, onChange }: { serverId: string; value: number | null; onChange: (v: number | null) => void }) {
  const [duplicate, setDuplicate] = useState(value !== null);
  const { data: issues, isLoading, isError } = useAidevIssues(serverId, duplicate);
  const radio = 'h-4 w-4 accent-[var(--color-brand)]';
  return (
    <fieldset className="min-w-0 space-y-1.5">
      <legend className="text-xs" style={{ color: 'var(--color-text-3)' }}>This issue is</legend>
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        <label className="flex items-center gap-2 py-1 text-sm" style={{ color: 'var(--color-text)' }}>
          <input type="radio" name="issue-kind" checked={!duplicate} onChange={() => { setDuplicate(false); onChange(null); }} className={radio} />
          New
        </label>
        <label className="flex items-center gap-2 py-1 text-sm" style={{ color: 'var(--color-text)' }}>
          <input type="radio" name="issue-kind" checked={duplicate} onChange={() => setDuplicate(true)} className={radio} />
          A duplicate of an open issue
        </label>
      </div>
      {duplicate && (
        <div className="space-y-1">
          <select value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)} aria-label="Issue to keep"
            className={`${field} sm:max-w-xl`} style={fieldStyle} disabled={isLoading || isError}>
            <option value="">{isLoading ? 'Loading the open issues…' : 'Pick the issue to keep'}</option>
            {(issues ?? []).map((i) => <option key={i.number} value={i.number}>#{i.number} {i.title}</option>)}
          </select>
          {isError && <p role="alert" className="text-xs" style={{ color: 'var(--color-error)' }}>The open issues could not be read. File it as new, or try again later.</p>}
          {value !== null && (
            <p className="text-xs" style={{ color: 'var(--color-text-2)' }}>
              It is filed, then closed at once as a duplicate of #{value}, which gets a note that {serverId} reported it too.
            </p>
          )}
        </div>
      )}
    </fieldset>
  );
}

/** The review before filing: the operator edits what will be public in the repository */
function Review({ serverId, p, pending, onOpen, onCancel }: { serverId: string; p: ImprovementProposal; pending: boolean; onOpen: (edits: { title: string; body: string; duplicateOf?: number }) => void; onCancel: () => void }) {
  const [title, setTitle] = useState(p.title);
  const [body, setBody] = useState(p.body);
  const [duplicateOf, setDuplicateOf] = useState<number | null>(null);
  const valid = title.trim().length >= 5 && body.trim().length >= 20;
  function submit(e: FormEvent) {
    e.preventDefault();
    if (valid) onOpen({ title: title.trim(), body: body.trim(), ...(duplicateOf !== null ? { duplicateOf } : {}) });
  }
  return (
    <form onSubmit={submit} className="mt-3 space-y-3">
      <p className="text-xs" style={{ color: 'var(--color-text-2)' }}>
        This becomes an issue on fd-management/aidev, seen by everyone with access to it. Take out anything that belongs to the client.
      </p>
      <DuplicateChoice serverId={serverId} value={duplicateOf} onChange={setDuplicateOf} />
      <label className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
        Title
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className={`${field} mt-1`} style={fieldStyle} autoFocus />
      </label>
      <label className="block text-xs" style={{ color: 'var(--color-text-3)' }}>
        Text
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} maxLength={20000} className={`${field} mt-1 max-w-[80ch] font-[inherit]`} style={fieldStyle} />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" className="max-sm:min-h-10" disabled={!valid || pending}>
          {pending ? 'Creating the issue…' : duplicateOf !== null ? `Create and close as duplicate of #${duplicateOf}` : 'Create the issue'}
        </Button>
        <Button type="button" size="sm" variant="ghost" className="max-sm:min-h-10" onClick={onCancel} disabled={pending}>Cancel</Button>
      </div>
    </form>
  );
}

function DuplicateLink({ n }: { n: number }) {
  return <a href={`https://github.com/fd-management/aidev/issues/${n}`} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>#{n}</a>;
}

function Outcome({ p }: { p: ImprovementProposal }) {
  if (p.status === 'opened') {
    return (
      <p className="text-sm" style={{ color: 'var(--color-success)' }}>
        Opened as{' '}
        <a href={p.issueUrl} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>issue #{p.issueNumber}</a>
        {p.duplicateOf ? <>, closed as duplicate of <DuplicateLink n={p.duplicateOf} /></> : null}.
      </p>
    );
  }
  if (p.status === 'dismissed') return <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>Dismissed: no issue was opened.</p>;
  if (p.status === 'opening') return <p className="text-sm" style={{ color: 'var(--color-text-2)' }}>Creating the issue…</p>;
  return null;
}

/** One proposal; `inSession` in the session that wrote it, which needs no link to itself (ADR-039 8c step 10) */
export function ProposalCard({ serverId, p, isAdmin, focused, onDecided, inSession = false }: { serverId: string; p: ImprovementProposal; isAdmin: boolean; focused: boolean; onDecided: (id: string) => void; inSession?: boolean }) {
  const decide = useDecideImprovement(serverId);
  const [reviewing, setReviewing] = useState(false);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }, [focused]);
  const current = decide.data?.improvement ?? p;
  const error = decide.error as Error | null;
  const done = (id: string) => () => onDecided(id);
  // A failed one is decided again: GitHub answered an error, the issue was not created
  const decidable = current.status === 'pending' || current.status === 'failed';

  return (
    <article ref={ref} id={`improvement-${p.id}`} aria-labelledby={`improvement-${p.id}-title`} className="card px-4 py-3"
      style={{ borderLeft: `3px solid ${EDGE[p.kind]}`, ...(focused ? { outline: '2px solid var(--color-brand-light)', outlineOffset: '3px' } : {}) }}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h3 id={`improvement-${p.id}-title`} className="min-w-0 break-words text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
          <span style={{ color: EDGE[p.kind] }}>{KIND[p.kind]}</span>{' '}{p.title}
        </h3>
        <span className="text-xs" style={{ color: 'var(--color-text-3)' }}>{ago(p.createdAt)}</span>
      </div>
      {!reviewing && <Body text={p.body} />}
      {p.requestedBySession && !reviewing && !inSession && (
        <p className="mt-1 text-xs" style={{ color: 'var(--color-text-2)' }}>
          Proposed by <Link to={`/servers/${serverId}/sessions/${p.requestedBySession}`} className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>a session</Link>.
        </p>
      )}

      <div className="mt-3">
        {current.status === 'failed' && (
          <p className="mb-2 text-sm" style={{ color: 'var(--color-error)' }}>The issue was not created: {current.error}.</p>
        )}
        {decidable && isAdmin && reviewing && (
          <Review serverId={serverId} p={p} pending={decide.isPending} onCancel={() => setReviewing(false)}
            onOpen={(edits) => decide.mutate({ id: p.id, decision: 'open', edits }, { onSuccess: done(p.id) })} />
        )}
        {decidable && isAdmin && !reviewing && (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" className="max-sm:min-h-10" onClick={() => setReviewing(true)}>Open as issue</Button>
            <Button size="sm" variant="ghost" className="max-sm:min-h-10" disabled={decide.isPending}
              onClick={() => decide.mutate({ id: p.id, decision: 'dismiss' }, { onSuccess: done(p.id) })}>Dismiss</Button>
          </div>
        )}
        {decidable && !isAdmin && <p className="text-xs" style={{ color: 'var(--color-text-3)' }}>Waiting for an admin of this server to decide.</p>}
        <Outcome p={current} />
        {error && <p role="alert" className="mt-2 text-xs" style={{ color: 'var(--color-error)' }}>Not decided: {error.message}</p>}
      </div>
    </article>
  );
}

const HISTORY_LABEL: Partial<Record<ImprovementProposal['status'], string>> = { opened: 'opened', dismissed: 'dismissed' };

/** `pending`: the proposals to decide (Sessions tab), nothing when none waits. `history`: decided ones (Settings). */
export function ImprovementProposals({ serverId, isAdmin, part = 'pending' }: { serverId: string; isAdmin: boolean; part?: 'pending' | 'history' }) {
  const { data } = useImprovements(serverId);
  const [params] = useSearchParams();
  const focus = params.get('improvement');
  const [decidedHere, setDecidedHere] = useState<Set<string>>(() => new Set());
  if (!data) return null;
  const open = data.filter((p) => p.status === 'pending' || p.status === 'opening' || p.status === 'failed' || decidedHere.has(p.id));
  const decided = data.filter((p) => !open.includes(p));

  if (part === 'history') {
    if (!decided.length) return null;
    return (
      <SettingsSection id="improvements-history-title" title="Proposals for aidev"
        state="Proposals from this server's sessions, once you opened them as issues or dismissed them.">
        <details className="mt-3" open={!!focus && decided.some((p) => p.id === focus)}>
          <summary className="cursor-pointer py-1.5 text-xs max-sm:min-h-10" style={{ color: 'var(--color-text-3)' }}>Decided in the last 30 days ({decided.length})</summary>
          <ul className="mt-2 space-y-1">
            {decided.map((p) => (
              <li key={p.id} id={`improvement-${p.id}`} className="w-fit max-w-full break-words rounded-sm px-1 text-xs" style={{ color: 'var(--color-text-2)', ...(p.id === focus ? { outline: '2px solid var(--color-brand-light)', outlineOffset: '3px' } : {}) }}>
                {KIND[p.kind]}: {p.title}, {HISTORY_LABEL[p.status] ?? p.status}
                {p.issueUrl && <>{' '}<a href={p.issueUrl} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>#{p.issueNumber}</a></>}
                {p.duplicateOf ? <>, duplicate of <DuplicateLink n={p.duplicateOf} /></> : null}
              </li>
            ))}
          </ul>
        </details>
      </SettingsSection>
    );
  }

  if (!open.length) return null;
  return (
    <section aria-labelledby="improvements-title" className="mb-8 min-w-0">
      <h2 id="improvements-title" className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Proposals for aidev</h2>
      <p className="mt-0.5 text-sm" style={{ color: 'var(--color-text-2)' }}>
        Written by this server&apos;s sessions. Nothing is filed until you open one as an issue.
      </p>
      <div className="mt-3 space-y-3">
        {open.map((p) => (
          <ProposalCard key={p.id} serverId={serverId} p={p} isAdmin={isAdmin} focused={p.id === focus}
            onDecided={(id) => setDecidedHere((prev) => new Set(prev).add(id))} />
        ))}
      </div>
    </section>
  );
}
